import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Brain, Gem, Store, Calculator, CalendarCheck, LineChart } from 'lucide-react';
import { clsx } from 'clsx';
export function Modules() {
  const [openIndex, setOpenIndex] = useState<number>(0);
  const modules = [{
    num: 'T1',
    Icon: Brain,
    title: 'La mentalidad del que vende',
    subtitle: 'La base que nadie te enseña',
    desc: 'Antes de hablar de proveedores y precios, hay que desactivar las creencias que te frenan. Este tomo convierte la duda en acción concreta. Incluye una calculadora de objetivo mensual y un contrato personal de primera venta.',
    bullets: ['Por qué el miedo a invertir te cuesta más de lo que creés', 'Calculadora de objetivo mensual realista para tu zona', 'Cómo pensar como vendedor desde el día 1, no desde el mes 6', 'Contrato personal de primera venta (con fecha límite)']
  }, {
    num: 'T2',
    Icon: Gem,
    title: 'Todo lo que tenés que saber del oro y las joyas',
    subtitle: 'Hablá con autoridad sin ser joyero',
    desc: 'El conocimiento mínimo imprescindible para no quedar mudo cuando un cliente te pregunta. Tipos de materiales, quilates, plata 925, bañado, laminado y cómo detectar oro falso.',
    bullets: ['Diferencias entre oro 18k, 14k, 10k, laminado y bañado', 'Cómo detectar oro falso sin instrumentos caros', 'Guión de respuestas listas para las 5 preguntas difíciles del cliente', 'Instructivo de cuidado de joyas para dar al comprador']
  }, {
    num: 'T3',
    Icon: Store,
    title: 'Dónde conseguir tu mercadería sin caer en una estafa',
    subtitle: 'Proveedores reales en Argentina',
    desc: 'El Once, los mayoristas de GBA, las opciones online y los proveedores del interior. Todo lo que necesitás saber antes de gastar un solo peso en mercadería.',
    bullets: ['4 canales de compra en Argentina con pros y contras de cada uno', 'Guía de campo para el Once: qué decir, cómo negociar, señales de alerta', 'Checklist de proveedor confiable con sistema de puntuación', 'Estrategia de 3 proveedores para no depender de uno solo']
  }, {
    num: 'T4',
    Icon: Calculator,
    title: 'Cómo ponerle precio sin regalar tu trabajo',
    subtitle: 'La fórmula del margen real',
    desc: 'La fórmula exacta para calcular tu precio de venta en cualquier canal. Nunca más ponés el precio a ojo. Nunca más te convencen de que "está caro".',
    bullets: ['Fórmula: Precio = Costo total × Multiplicador (×3 recomendado)', 'Tabla de precios por canal: feria, redes, WhatsApp, Mercado Libre', '5 respuestas probadas cuando te dicen "está caro"', 'Planilla de precios lista para completar con tu mercadería']
  }, {
    num: 'T5',
    Icon: CalendarCheck,
    title: 'Tu primera venta esta semana',
    subtitle: 'Plan de 7 días con tareas concretas',
    desc: 'El plan de acción diario más directo que existe: del Día 1 al Día 7, con una tarea específica cada día. Al final de la semana tenés tu primera venta cobrada o sabés exactamente por qué no pasó.',
    bullets: ['Día 1: elegí tu producto héroe (el que vende primero)', 'Día 3: primer contacto con proveedor (guión incluido)', 'Día 5: primera publicación lista', 'Día 7: primera venta cobrada y empacada']
  }, {
    num: 'T6',
    Icon: LineChart,
    title: 'Cómo manejar tu negocio sin perderte en los números',
    subtitle: 'Control en 10 minutos por día',
    desc: 'El sistema mínimo de administración para saber cuánto ganaste, qué te falta reponer y cómo está la caja, sin ser contador ni usar software caro.',
    bullets: ['3 planillas listas: registro de ventas, control de stock y flujo de caja', 'Panel de 5 indicadores que te dicen si el negocio va bien o no', 'Rutina de 10 minutos diarios para mantener todo en orden', 'Cuándo sabés que es momento de reinvertir en más mercadería']
  }];
  return <section className="py-12 sm:py-20" data-id="element-192">
      <div className="mx-auto max-w-[800px] px-6" data-id="element-193">
        <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-gold" data-id="element-194">
          Contenido completo
        </div>
        <h2 className="mb-4 font-serif text-[clamp(24px,4vw,38px)] font-bold leading-[1.2] text-white" data-id="element-195">
          Los 6 Tomos Base
        </h2>
        <p className="mb-8 sm:mb-12 max-w-[560px] text-[15px] text-text" data-id="element-196">
          Tu hoja de ruta para arrancar y hacer tu primera venta en 30 días.
          Tocá cada tomo para ver lo que incluye.
        </p>

        <div className="flex flex-col gap-2.5" data-id="element-197">
          {modules.map((mod, i) => {
          const isOpen = openIndex === i;
          const {
            Icon
          } = mod;
          return <div key={i} className={clsx('overflow-hidden rounded-xl border bg-card transition-all duration-300', isOpen ? 'border-l-2 border-gold shadow-[0_0_24px_rgba(201,168,76,0.15)]' : 'border-border hover:border-gold/40')} data-id="element-198">
                <button type="button" onClick={() => setOpenIndex(isOpen ? -1 : i)} aria-expanded={isOpen} className="flex w-full items-center gap-3 p-3 text-left sm:gap-4 sm:p-4" data-id="element-199">
                  {/* Representative icon tile (SVG — instant load) */}
                  <motion.div animate={{
                backgroundColor: isOpen ? '#C9A84C' : 'rgba(201,168,76,0.10)'
              }} transition={{
                duration: 0.3
              }} className={clsx('flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border sm:h-12 sm:w-12', isOpen ? 'border-gold' : 'border-gold/20')} data-id="element-200">
                    <Icon className={clsx('h-5 w-5 sm:h-6 sm:w-6', isOpen ? 'text-[#111]' : 'text-gold')} strokeWidth={1.75} data-id="element-201" />
                  </motion.div>

                  <div className="flex-1 min-w-0" data-id="element-202">
                    <div className="flex items-center gap-2" data-id="element-203">
                      <span className={clsx('shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold tracking-[0.04em]', isOpen ? 'bg-gold text-[#111]' : 'bg-gold/10 text-gold')} data-id="element-204">
                        {mod.num}
                      </span>
                      <h3 className={clsx('truncate text-[14px] font-semibold sm:text-[15px]', isOpen ? 'text-gold' : 'text-white')} data-id="element-205">
                        {mod.title}
                      </h3>
                    </div>
                    <p className="mt-0.5 truncate text-[11px] text-muted sm:text-[12px]" data-id="element-206">
                      {mod.subtitle}
                    </p>
                  </div>

                  <motion.div animate={{
                rotate: isOpen ? 180 : 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-207">
                    <ChevronDown className="h-4 w-4 shrink-0 text-gold sm:h-5 sm:w-5" data-id="element-208" />
                  </motion.div>
                </button>

                <AnimatePresence initial={false} data-id="element-209">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-210">
                      <div className="border-t border-gold/20 px-4 pb-4 pt-3 sm:px-5 sm:pb-5 sm:pl-[72px]" data-id="element-211">
                        <p className="mb-3 text-[13px] leading-[1.7] text-text" data-id="element-212">
                          {mod.desc}
                        </p>
                        <ul className="flex flex-col gap-1.5 sm:gap-2" data-id="element-213">
                          {mod.bullets.map((bullet, idx) => <li key={idx} className="flex items-start gap-2 text-[12px] text-muted" data-id="element-214">
                              <span className="shrink-0 text-gold" data-id="element-215">▸</span>
                              {bullet}
                            </li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>
    </section>;
}